// Zugbaum wie im lichess-Analysebrett: Hauptvariante aus der Partie, eigene Varianten daneben.
// Züge werden in chessops-Form (Rochade als König-schlägt-Turm, z. B. e1h1) gespeichert,
// so wie scalachess sie liefert.
import { Chess, normalizeMove } from 'chessops/chess';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSan } from 'chessops/san';
import { makeUci, parseUci } from 'chessops/util';
import type { Ply } from './api';

export interface Node {
  id: number;
  ply: number;
  fen: string;
  uci?: string;
  san?: string;
  check: boolean;
  /** true: gehört zur Partie, nicht zu einer eigenen Variante */
  game: boolean;
  parent?: Node;
  children: Node[];
}

let nextId = 1;

function position(fen: string): Chess {
  return Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
}

export function root(fen: string): Node {
  const pos = position(fen);
  const ply = (pos.fullmoves - 1) * 2 + (pos.turn === 'white' ? 0 : 1);
  return { id: nextId++, ply, fen, check: pos.isCheck(), game: true, children: [] };
}

/** Baum aus den Halbzügen, die der Server (scalachess) geliefert hat. */
export function fromPlies(plies: Ply[]): Node {
  const r = root(plies[0].fen);
  let cur = r;
  for (const p of plies.slice(1)) {
    const n: Node = {
      id: nextId++,
      ply: cur.ply + 1,
      fen: p.fen,
      uci: p.uci ?? undefined,
      san: p.san ?? undefined,
      check: !!p.san && /[+#]$/.test(p.san),
      game: true,
      parent: cur,
      children: [],
    };
    cur.children.push(n);
    cur = n;
  }
  return r;
}

/**
 * Zug an einem Knoten ausführen. Gibt es ihn schon als Kind, wird dieses zurückgegeben,
 * sonst entsteht eine neue Variante. null bei ungültigem Zug.
 */
export function play(node: Node, uci: string): Node | null {
  const pos = position(node.fen);
  const raw = parseUci(uci);
  if (!raw) return null;
  const move = normalizeMove(pos, raw);
  if (!pos.isLegal(move)) return null;
  const norm = makeUci(move);
  const existing = node.children.find(c => c.uci === norm);
  if (existing) return existing;
  const san = makeSan(pos, move);
  pos.play(move);
  const child: Node = {
    id: nextId++,
    ply: node.ply + 1,
    fen: makeFen(pos.toSetup()),
    uci: norm,
    san,
    check: pos.isCheck(),
    game: false,
    parent: node,
    children: [],
  };
  node.children.push(child);
  return child;
}

export function pathTo(node: Node): Node[] {
  const out: Node[] = [];
  for (let n: Node | undefined = node; n; n = n.parent) out.unshift(n);
  return out;
}

/** Erste Kinder bis zum Ende: die Linie, auf der man mit → weitergeht. */
export function lineFrom(node: Node): Node[] {
  const out = [node];
  let n = node;
  while (n.children.length) {
    n = n.children[0];
    out.push(n);
  }
  return out;
}

export function remove(node: Node) {
  const p = node.parent;
  if (!p || node.game) return;
  p.children = p.children.filter(c => c !== node);
}

/** Eigene Varianten als Zugfolgen ab der Wurzel (für den Server). */
export function variationLines(r: Node): string[][] {
  const lines: string[][] = [];
  const walk = (n: Node) => {
    if (!n.children.length && !n.game) {
      lines.push(pathTo(n).slice(1).map(x => x.uci!));
    }
    n.children.forEach(walk);
  };
  walk(r);
  return lines;
}

export function addLines(r: Node, lines: string[][]) {
  for (const line of lines) {
    let n: Node | null = r;
    for (const uci of line) {
      n = n && play(n, uci);
      if (!n) break;
    }
  }
}

/** Zugnummer mit Punkten: "12." für Weiß, "12…" für Schwarz */
export function moveLabel(n: Node): string {
  const no = Math.ceil(n.ply / 2);
  return n.ply % 2 === 1 ? `${no}.` : `${no}…`;
}
